import invariant from 'invariant';
import { useCallback, useState } from 'react';
import { positions } from '../defaultPosition';
import { fitsOnBottom, fitsOnLeft, fitsOnRight, fitsOnTop, getDefaultAlign, isPositionedToBottom, isPositionedToLeft, isPositionedToRight, isPositionedToTop } from '../utils';

var useAutoFlip = function useAutoFlip(enableAutoHorizontalFlip, enableAutoVerticalFlip, initialPosition, initialAlign) {
  var _useState = useState(initialPosition),
      currentPosition = _useState[0],
      setCurrentPosition = _useState[1];

  var _useState2 = useState(initialAlign || getDefaultAlign(initialPosition)),
      currentAlign = _useState2[0],
      setCurrentAlign = _useState2[1];

  var checkAlignment = useCallback(function (nodeRef, anchorRef) {
    var _autoFlipHorizontally = autoFlipHorizontally(nodeRef, anchorRef, {
      enabled: enableAutoHorizontalFlip,
      initialPosition: initialPosition,
      initialAlign: initialAlign,
      currentPosition: currentPosition,
      currentAlign: currentAlign
    }),
        horizontalPosition = _autoFlipHorizontally[0],
        horizontalAlign = _autoFlipHorizontally[1];

    var _autoFlipVertically = autoFlipVertically(nodeRef, anchorRef, {
      enabled: enableAutoVerticalFlip,
      initialPosition: initialPosition,
      initialAlign: initialAlign,
      currentPosition: horizontalPosition,
      currentAlign: horizontalAlign
    }),
        verticalPosition = _autoFlipVertically[0],
        verticalAlign = _autoFlipVertically[1];

    if (verticalPosition !== currentPosition) {
      setCurrentPosition(verticalPosition);
    }

    if (verticalAlign !== currentAlign) {
      setCurrentAlign(verticalAlign);
    }
  }, [currentAlign, currentPosition, enableAutoHorizontalFlip, enableAutoVerticalFlip, initialAlign, initialPosition]);
  return [currentPosition, currentAlign, checkAlignment];
};

export default useAutoFlip;

var autoFlipVertically = function autoFlipVertically(nodeRef, anchorRef, _ref) {
  var enabled = _ref.enabled,
      initialPosition = _ref.initialPosition,
      currentPosition = _ref.currentPosition,
      initialAlign = _ref.initialAlign,
      currentAlign = _ref.currentAlign;

  if (!enabled) {
    return [currentPosition, currentAlign];
  }

  var positionedToBottom = isPositionedToBottom(currentPosition);
  var positionedToTop = isPositionedToTop(currentPosition);

  if (isPositionedToBottom(initialPosition)) {
    if (fitsOnBottom(nodeRef, anchorRef)) {
      if (!positionedToBottom) {
        return [switchToBottom(currentPosition), switchToTop(currentAlign)];
      }
    } else if (fitsOnTop(nodeRef, anchorRef) && !positionedToTop) {
      return [switchToTop(currentPosition), switchToBottom(currentAlign)];
    }
  }

  if (isPositionedToTop(initialPosition)) {
    if (fitsOnTop(nodeRef, anchorRef)) {
      if (!positionedToTop) {
        return [switchToTop(currentPosition), switchToBottom(currentAlign)];
      }
    } else if (fitsOnBottom(nodeRef, anchorRef) && !positionedToBottom) {
      return [switchToBottom(currentPosition), switchToTop(currentAlign)];
    }
  }

  return [currentPosition, currentAlign];
};

var autoFlipHorizontally = function autoFlipHorizontally(nodeRef, anchorRef, _ref2) {
  var enabled = _ref2.enabled,
      initialPosition = _ref2.initialPosition,
      currentPosition = _ref2.currentPosition,
      initialAlign = _ref2.initialAlign,
      currentAlign = _ref2.currentAlign;

  if (!enabled) {
    return [currentPosition, currentAlign];
  }

  var positionedToLeft = isPositionedToLeft(currentPosition);
  var positionedToRight = isPositionedToRight(currentPosition);

  if (isPositionedToRight(initialPosition)) {
    if (fitsOnRight(nodeRef, anchorRef)) {
      if (!positionedToRight) {
        return [switchToRight(currentPosition), switchToLeft(currentAlign)];
      }
    } else if (fitsOnLeft(nodeRef, anchorRef) && !positionedToLeft) {
      return [switchToLeft(currentPosition), switchToRight(currentAlign)];
    }
  }

  if (isPositionedToLeft(initialPosition)) {
    if (fitsOnLeft(nodeRef, anchorRef)) {
      if (!positionedToLeft) {
        return [switchToLeft(currentPosition), switchToRight(currentAlign)];
      }
    } else if (fitsOnRight(nodeRef, anchorRef) && !positionedToRight) {
      return [switchToRight(currentPosition), switchToLeft(currentAlign)];
    }
  }

  return [currentPosition, currentAlign];
};

var switchVerticalPosition = function switchVerticalPosition(position, target) {
  var newPosition = positions.find(function (standardPosition) {
    return standardPosition === target + " " + position.split(' ')[1];
  });
  invariant(newPosition, "Could not determine new position. Old position \"" + position + "\", new vertical target \"" + target + "\"");
  return newPosition;
};

var switchHorizontalPosition = function switchHorizontalPosition(position, target) {
  var newPosition = positions.find(function (standardPosition) {
    return standardPosition === position.split(' ')[0] + " " + target;
  });
  invariant(newPosition, "Could not determine new position. Old position \"" + position + "\", new horizontal target \"" + target + "\"");
  return newPosition;
};

var switchToBottom = function switchToBottom(position) {
  return switchVerticalPosition(position, 'bottom');
};

var switchToTop = function switchToTop(position) {
  return switchVerticalPosition(position, 'top');
};

var switchToLeft = function switchToLeft(position) {
  return switchHorizontalPosition(position, 'left');
};

var switchToRight = function switchToRight(position) {
  return switchHorizontalPosition(position, 'right');
};